import type { Directive, DirectiveBinding } from 'vue'

/**
 * Vue directive: run a handler when a pointer press lands outside the element.
 *
 * Usage:
 *   <div v-click-outside="close"> … </div>
 *
 * Bind it to the element that wraps BOTH the trigger and the panel it opens.
 * Binding it to the panel alone makes the trigger "outside", so pressing it
 * would close the panel and the trigger's own click would immediately reopen
 * it — the panel appears stuck.
 *
 * Listens on `pointerdown`, not `click`:
 *
 *  - It fires before focus moves and before a menu can re-render, so a press
 *    that removes its own target still registers.
 *  - It covers mouse, touch and pen without a second touch handler.
 *
 * The listener is attached in the CAPTURE phase so a child calling
 * `stopPropagation` (date pickers commonly do) cannot swallow it.
 */
type ClickOutsideHandler = (event: PointerEvent) => void

interface ClickOutsideElement extends HTMLElement {
  __clickOutside__?: (event: PointerEvent) => void
}

export const vClickOutside: Directive<ClickOutsideElement, ClickOutsideHandler> = {
  mounted(el: ClickOutsideElement, binding: DirectiveBinding<ClickOutsideHandler>) {
    const handler = (event: PointerEvent) => {
      if (typeof binding.value !== 'function') return

      const target = event.target as Node | null
      // A target detached from the document (a cell the picker just re-rendered)
      // would fail `contains` and read as an outside click.
      if (!target || !target.isConnected) return
      if (el === target || el.contains(target)) return

      binding.value(event)
    }

    el.__clickOutside__ = handler
    document.addEventListener('pointerdown', handler, true)
  },

  unmounted(el: ClickOutsideElement) {
    if (el.__clickOutside__) {
      document.removeEventListener('pointerdown', el.__clickOutside__, true)
      delete el.__clickOutside__
    }
  },
}

export default vClickOutside
